import React, { useEffect, useState } from 'react';
import { Dimensions, Coordinates } from 'constants/';
import { Image } from 'react-konva';
import WellService from 'business/services/WellService';
import { useImageStoreImageByWellParamsKey } from 'redux/hooks/well';
import { getWellDropImageUniqueKey } from 'redux/utils/well';
import { IWellImageParams } from 'business/models/Models';
import { EFetchingStatus } from 'enums/EFetchingStatus';

export interface Props {
  canvasCoordinatesToRenderOn?: Coordinates;
  dimensions?: Dimensions;
  captureProfileId: number;
  inspectionDate?: Date;
  plateId: number;
  wellNumber: number;
  batchId: number;
  dropNumber: number;
  regionId: number;
  regionName?: string;
  useCanvasImage?: boolean;
  handleOnImageLoad?: () => void;
  fetchThumbnail?: boolean;
}

const WellDropImageLoader = (props: Props) => {
  const {
    canvasCoordinatesToRenderOn,
    dimensions,
    plateId,
    wellNumber,
    batchId,
    captureProfileId,
    dropNumber,
    regionId,
    useCanvasImage = true,
    handleOnImageLoad,
    fetchThumbnail = true,
  } = props;

  const imageParams: IWellImageParams = {
    wellNumber,
    dropNumber,
    pid: plateId,
    cpID: captureProfileId,
    batchId: batchId,
    regionID: regionId,
    thumbnailImageType: 'th',
  };
  const [image, fetchingStatus] = useImageStoreImageByWellParamsKey(
    getWellDropImageUniqueKey(imageParams),
  );
  const [imageDetails, setImageDetails] = useState(null);

  useEffect(() => {
    const getImage = async () => {
      const _wellService = new WellService();
      _wellService
        .getWellImage(
          plateId,
          wellNumber,
          batchId,
          dropNumber,
          captureProfileId,
          regionId,
          false,
          fetchThumbnail ? 'th' : '',
          false,
        )
        .then((wellImage) => {
          const img = new window.Image();
          img.src = wellImage.wellImage;
          img.onload = () => setImageDetails({ data: img });
        });
    };

    if (
      /**
       * this should be called if somehow(unknown reason/bug) batching image request is failing/not working
       * the reason image is being compare with undefined(and not null) is that,
       * the request was not sent or somehow missed in batch. so, we send it separately.
       */
      image === undefined &&
      fetchingStatus !== EFetchingStatus.InProgress &&
      batchId &&
      captureProfileId &&
      dropNumber &&
      regionId &&
      plateId &&
      wellNumber
    ) {
      getImage();
    } else if (image) {
      const img = new window.Image();
      img.src = image;
      img.onload = () => {
        setImageDetails({ data: img });
        handleOnImageLoad && handleOnImageLoad();
      };
    } else {
      handleOnImageLoad && handleOnImageLoad();
    }
  }, [props, fetchingStatus]);

  if (!imageDetails?.data) {
    return null;
  }

  if (!useCanvasImage) {
    return (
      <img
        src={imageDetails.data.src}
        alt={'Well Drop Image '}
        className="h-full"
        style={dimensions ? { height: dimensions.height, width: dimensions.width } : {}}
      />
    );
  }
  // calculating image height keeping aspect ratio intact
  const imageHeight =
    dimensions.width / (imageDetails.data.width / imageDetails.data.height);

  return (
    <Image
      id="welldropimageloader-image"
      x={canvasCoordinatesToRenderOn.x}
      y={canvasCoordinatesToRenderOn.y + (dimensions.height - imageHeight) / 2}
      height={imageHeight}
      width={dimensions.width}
      image={imageDetails.data}
    />
  );
};

export default WellDropImageLoader;
